import { useEffect, useMemo, type CSSProperties, type DragEvent } from "react";
import {
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  MarkerType,
  MiniMap,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  type Connection,
  type Edge,
  type Node,
  type NodeHandle,
  type NodeProps,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { missingPorts } from "./requiredPorts";
import { nodePorts, nodeSize, nodeSummary, nodeTitle, portLabel, portOffset } from "./nodeDisplay";
import { nodeTypeStyle } from "./nodeTypeStyle";
import type { FlowDocument, FlowNode, FlowNodePosition } from "./types";

/** The drag payload a Palette item carries: the node type to add. */
export const PALETTE_DRAG_TYPE = "application/x-uvi-node-type";

interface FlowCanvasProps {
  document: FlowDocument;
  positions: Record<string, FlowNodePosition>;
  selectedNodeId?: string | null;
  selectedEdgeId?: string | null;
  /** The node a running simulation (Simulator.tsx) is currently sitting on. */
  activeNodeId?: string | null;
  onSelectNode?: (nodeId: string | null) => void;
  onSelectEdge?: (edgeId: string | null) => void;
  onNodeDragStop?: (nodeId: string, position: FlowNodePosition) => void;
  /** A new edge from `source`'s own `port` handle to `target`. */
  onConnect?: (source: string, port: string, target: string) => void;
  /** A palette item dropped onto the canvas at `position` (flow coordinates). */
  onDropNode?: (type: string, position: FlowNodePosition) => void;
  /**
   * Viewing a published version rather than editing a draft: the graph is
   * still pannable, zoomable and selectable, but cannot be changed. A
   * published version is immutable, so a canvas that accepted edits here
   * would be offering something the server will not store.
   */
  readOnly?: boolean;
}

interface IvrNodeData extends Record<string, unknown> {
  node: FlowNode;
  ports: string[];
  missing: string[];
  isStart: boolean;
  isSelected: boolean;
  isActive: boolean;
}

type IvrNode = Node<IvrNodeData, "ivr">;

// Must match the handle size and input-handle offset in app.css (.ivr-node).
const HANDLE_SIZE = 10;
const INPUT_HANDLE_TOP = 18;

/**
 * Every handle's position on the card (and, in FlowCanvasInner, the card's
 * own size), declared up front instead of left to React Flow to measure. React Flow only draws an edge once it knows
 * both ends' handle bounds, and it re-derives those from the node objects
 * it is given: this component rebuilds its nodes on every document change
 * without ever receiving the measured result back (there is no
 * onNodesChange round trip — the document is the source of truth), so
 * measured bounds kept being reset, and because the cards' size never
 * changes, nothing triggered a re-measure — the canvas silently drew no
 * edges at all. The card layout is fixed per port row (nodeDisplay.ts), so
 * the positions are known exactly.
 */
function nodeHandles(isStart: boolean, ports: string[], width: number): NodeHandle[] {
  const handles: NodeHandle[] = ports.map((port, index) => ({
    id: port,
    type: "source",
    position: Position.Right,
    x: width - HANDLE_SIZE / 2,
    y: portOffset(index) - HANDLE_SIZE / 2,
    width: HANDLE_SIZE,
    height: HANDLE_SIZE,
  }));
  if (!isStart) {
    handles.push({
      id: "in",
      type: "target",
      position: Position.Left,
      x: -HANDLE_SIZE / 2,
      y: INPUT_HANDLE_TOP - HANDLE_SIZE / 2,
      width: HANDLE_SIZE,
      height: HANDLE_SIZE,
    });
  }
  return handles;
}

const SELECTED_EDGE_COLOR = "#465fff";
const DEFAULT_EDGE_COLOR = "#98a2b3";

/**
 * One node as a card: a coloured header with the type's own title and the
 * node id, a one-line summary of its config, then one row per outgoing
 * port with that port's own source handle beside its label. Connecting is
 * therefore port-aware: dragging from "Machine" creates a `machine` edge,
 * not a generic one that has to be renamed afterwards. Every node except
 * `start` has a single input handle on its left edge.
 */
function IvrNodeCard({ data }: NodeProps<IvrNode>) {
  const { node, ports, missing, isStart, isSelected, isActive } = data;
  const classes = ["ivr-node"];
  if (isStart) classes.push("ivr-node--start");
  if (isSelected) classes.push("ivr-node--selected");
  if (isActive) classes.push("ivr-node--active");
  if (missing.length > 0) classes.push("ivr-node--missing-port");
  const style = { "--node-color": nodeTypeStyle(node.type).color } as CSSProperties;

  return (
    <div className={classes.join(" ")} style={style}>
      {node.type !== "start" ? <Handle type="target" position={Position.Left} id="in" className="ivr-node__in" /> : null}
      <div className="ivr-node__header">
        <span className="ivr-node__title">{nodeTitle(node.type)}</span>
        <span className="ivr-node__id">{node.id}</span>
      </div>
      <p className="ivr-node__summary" title={nodeSummary(node)}>
        {nodeSummary(node)}
      </p>
      {ports.length > 0 ? (
        <ul className="ivr-node__ports">
          {ports.map((port, index) => {
            const isMissing = missing.includes(port);
            return (
              <li key={port} className={`ivr-node__port${isMissing ? " ivr-node__port--missing" : ""}`}>
                {portLabel(port)}
                {isMissing ? (
                  <span className="sr-only"> (not connected)</span>
                ) : null}
                <Handle
                  type="source"
                  position={Position.Right}
                  id={port}
                  className="ivr-node__out"
                  style={{ top: portOffset(index) }}
                />
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

// Defined once at module scope: React Flow re-creates every node whenever
// this object's identity changes.
const NODE_TYPES = { ivr: IvrNodeCard };

/**
 * Refits the viewport whenever `refitKey` changes (the document's own id
 * combined with its current node count — either a full document swap or
 * an add/remove), not on every render: a plain `fitView` boolean prop on
 * `<ReactFlow>` only fits once, on mount. Without it, a node added after
 * mount can land outside the visible, clipped canvas. Deliberately NOT
 * keyed on the full node list: dragging or editing a node's config must
 * not yank the user's own pan/zoom back to fit-everything.
 *
 * `minZoom` on `<ReactFlow>` is lowered below its 0.5 default for the
 * same reason: past 0.5x, fitView cannot zoom out far enough to fit a wide
 * layout and leaves nodes off the edge.
 */
function FitViewOnNodeCountChange({ refitKey }: { refitKey: string }) {
  const { fitView } = useReactFlow();
  useEffect(() => {
    fitView({ duration: 200, padding: 0.15 });
    // fitView's own identity is stable across renders (from useReactFlow),
    // so refitKey is the only real dependency here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refitKey]);
  return null;
}

/**
 * Renders a flow document with React Flow: click to select a node or edge,
 * drag to reposition, drag from a port to another node's input to
 * connect, drop a palette item to add.
 *
 * Positioning is the caller's concern (FlowPreview computes it via
 * layoutFlow and owns what a drag commits back to the document) — this
 * component only renders whatever `positions` it is given and reports
 * clicks/drags/connects/drops upward.
 *
 * Every node whose required ports (requiredPorts.ts, mirroring Go's real
 * per-node-type port table) are not all wired gets a dashed warning
 * border, and each unwired port row is marked too — recomputed from
 * `document` on every render, so it stays correct through adds, removes,
 * reconnects and config edits (a gather node's port list depends on its
 * own `valid_keys`).
 *
 * `activeNodeId` is a separate highlight from `selectedNodeId`: a running
 * simulation marks the node it is sitting on without selecting anything.
 *
 * Wrapped in its own `ReactFlowProvider`: `useReactFlow()` (fitView,
 * screenToFlowPosition) only works inside one.
 */
export function FlowCanvas(props: FlowCanvasProps) {
  return (
    <ReactFlowProvider>
      <FlowCanvasInner {...props} />
    </ReactFlowProvider>
  );
}

function FlowCanvasInner({
  document,
  positions,
  selectedNodeId,
  selectedEdgeId,
  activeNodeId,
  onSelectNode,
  onSelectEdge,
  onNodeDragStop,
  onConnect,
  onDropNode,
  readOnly = false,
}: FlowCanvasProps) {
  const { screenToFlowPosition } = useReactFlow();

  const nodes: IvrNode[] = useMemo(
    () =>
      document.nodes.map((node) => {
        // Explicit width/height rather than auto-measurement: MiniMap's own
        // node filter falls back to node.width/height for controlled nodes,
        // and without them it silently draws no node rects at all.
        const size = nodeSize(document, node);
        const ports = nodePorts(document, node);
        const isStart = node.id === document.start_node_id;
        return {
          id: node.id,
          type: "ivr" as const,
          position: positions[node.id] ?? { x: 0, y: 0 },
          width: size.width,
          height: size.height,
          // Declared, like the handles below and for the same reason: fitView
          // only counts nodes with measured dimensions, and every rebuild of
          // this list would otherwise reset them, leaving fitView to frame
          // whichever few cards happened to be re-measured.
          measured: size,
          handles: nodeHandles(isStart, ports, size.width),
          data: {
            node,
            ports,
            missing: missingPorts(document, node.id),
            isStart,
            isSelected: node.id === selectedNodeId,
            isActive: node.id === activeNodeId,
          },
          draggable: true,
          connectable: true,
        };
      }),
    [document, positions, selectedNodeId, activeNodeId],
  );

  const edges: Edge[] = useMemo(
    () =>
      document.edges.map((edge) => {
        const color = edge.id === selectedEdgeId ? SELECTED_EDGE_COLOR : DEFAULT_EDGE_COLOR;
        return {
          id: edge.id,
          source: edge.source,
          sourceHandle: edge.port,
          target: edge.target,
          targetHandle: "in",
          type: "smoothstep",
          style: { stroke: color, strokeWidth: edge.id === selectedEdgeId ? 2.5 : 1.5 },
          markerEnd: { type: MarkerType.ArrowClosed, color },
        };
      }),
    [document, selectedEdgeId],
  );

  function handleConnect(connection: Connection) {
    if (connection.source && connection.target && connection.sourceHandle) {
      onConnect?.(connection.source, connection.sourceHandle, connection.target);
    }
  }

  function clearSelection() {
    onSelectNode?.(null);
    onSelectEdge?.(null);
  }

  function handleDragOver(event: DragEvent) {
    if (readOnly) return;
    if (event.dataTransfer.types.includes(PALETTE_DRAG_TYPE)) {
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
    }
  }

  function handleDrop(event: DragEvent) {
    if (readOnly) return;
    const type = event.dataTransfer.getData(PALETTE_DRAG_TYPE);
    if (!type) return;
    event.preventDefault();
    onDropNode?.(type, screenToFlowPosition({ x: event.clientX, y: event.clientY }));
  }

  return (
    <div
      className="flow-canvas"
      aria-label={`Flow graph for ${document.id}`}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={NODE_TYPES}
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        elementsSelectable
        minZoom={0.05}
        colorMode="light"
        proOptions={{ hideAttribution: true }}
        /* Each handler reports only its own kind of selection — the
           caller's onSelectNode/onSelectEdge already clear the other side
           themselves. Calling both here too would double-clear: a node
           click would set selectedNodeId, then immediately reset it via
           the caller's own "also clear the other" side effect. */
        onNodeClick={(_, node) => onSelectNode?.(node.id)}
        onEdgeClick={(_, edge) => onSelectEdge?.(edge.id)}
        onPaneClick={clearSelection}
        onNodeDragStop={(_, node) => onNodeDragStop?.(node.id, node.position)}
        /* A no-op rather than an absent handler: React Flow's own prop
           type does not accept undefined, and nodesConnectable is already
           false here, so this can never actually fire. */
        onConnect={readOnly ? () => {} : handleConnect}
      >
        <FitViewOnNodeCountChange refitKey={`${document.id}:${nodes.length}`} />
        <Background variant={BackgroundVariant.Dots} gap={18} size={1.2} />
        <Controls showInteractive={false} />
        <MiniMap
          pannable
          zoomable
          bgColor="transparent"
          maskColor="rgba(16, 24, 40, 0.08)"
          nodeColor={(node) => {
            const data = node.data as IvrNodeData | undefined;
            return data ? nodeTypeStyleHex(data.node.type) : DEFAULT_EDGE_COLOR;
          }}
          nodeBorderRadius={6}
        />
      </ReactFlow>
    </div>
  );
}

// MiniMap paints node rects through SVG `fill` attributes, which cannot
// resolve CSS custom properties, so it gets plain hex per category.
const MINIMAP_COLORS: Record<string, string> = {
  "Flow control": "#667085",
  Messaging: "#465fff",
  Input: "#9333ea",
  Compliance: "#db2777",
  Data: "#4f46e5",
  Integration: "#0891b2",
};

function nodeTypeStyleHex(type: string): string {
  return MINIMAP_COLORS[nodeTypeStyle(type).category] ?? DEFAULT_EDGE_COLOR;
}
